/**
 * Geographic distance helpers.
 *
 * Uses the haversine formula to compute great-circle distance between two
 * latitude/longitude points. This is an approximation (Earth treated as a
 * sphere) but is accurate to within ~0.5% — more than enough for a
 * "within X miles" filter in a consumer marketplace.
 */

const EARTH_RADIUS_MILES = 3958.8;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

export interface LatLng {
  latitude: number;
  longitude: number;
}

export function haversineMiles(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Fallback: US ZIP → lat/lng via Zippopotam (no API key, works when Google
 * Maps JS is not loaded — e.g. local dev without REACT_APP_GOOGLE_MAPS_API_KEY).
 */
async function geocodeZipZippopotam(zip: string): Promise<LatLng | null> {
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      places?: Array<{ latitude?: string; longitude?: string }>;
    };
    const place = data?.places?.[0];
    if (place?.latitude == null || place?.longitude == null) return null;
    const lat = parseFloat(String(place.latitude));
    const lng = parseFloat(String(place.longitude));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { latitude: lat, longitude: lng };
  } catch {
    return null;
  }
}

/**
 * Geocode a US 5-digit ZIP to lat/lng.
 * Tries Google Maps Geocoder when `google.maps` is available; otherwise falls
 * back to Zippopotam so distance filters work without loading the Maps SDK.
 */
export async function geocodeZip(zip: string): Promise<LatLng | null> {
  if (!/^\d{5}$/.test(zip)) return null;

  if (typeof google !== "undefined" && google.maps?.Geocoder) {
    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({
        componentRestrictions: { country: "US", postalCode: zip },
      });
      const result = response.results[0];
      if (result) {
        const loc = result.geometry.location;
        return { latitude: loc.lat(), longitude: loc.lng() };
      }
    } catch {
      // Fall through to Zippopotam (e.g. billing / quota issues).
    }
  }

  return geocodeZipZippopotam(zip);
}
